if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.lightboxInitialized = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        
        this.isCustomLayout = this.classList.contains('product__media-gallery--custom-layout');
        
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.isCustomLayout) {
            this.destroySlider();
          } else if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        if (this.lightboxInitialized) return;
        
        // Wait for PhotoSwipe to load
        if (!window.FoxTheme.PhotoSwipeLightbox) {
          setTimeout(() => this.initImageZoom(), 100);
          return;
        }
        
        this.lightboxInitialized = true;

        const self = this;
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        
        if (!allMedia || allMedia.length === 0) return;

        allMedia.forEach((media, index) => {
          const mediaType = media.dataset.mediaType || 'image';
          
          switch (mediaType) {
            case 'model':
              const modelEl = media.querySelector('product-model');
              if (modelEl) {
                dataSource.push({
                  id: index,
                  html: `<div class="pswp__item--model">${modelEl.outerHTML}</div>`,
                  mediaType: 'model',
                });
              }
              break;
            case 'video':
            case 'external_video':
              const videoEl = media.querySelector('video-element, .video-element');
              if (videoEl) {
                dataSource.push({
                  id: index,
                  html: `<div class="pswp__item--video">${videoEl.outerHTML}</div>`,
                  mediaType: mediaType,
                });
              }
              break;
            case 'image':
            default:
              const imgSrc = media.dataset.src || media.querySelector('img')?.src;
              const width = parseInt(media.dataset.pswpWidth) || 1200;
              const height = parseInt(media.dataset.pswpHeight) || 1200;
              
              if (imgSrc) {
                dataSource.push({
                  id: index,
                  src: imgSrc,
                  width: width,
                  height: height,
                  mediaType: 'image',
                });
              }
              break;
          }
        });

        if (dataSource.length === 0) return;

        // Initialize PhotoSwipe Lightbox
        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 0.9,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          padding: { top: 60, bottom: 60, left: 60, right: 60 },
          initialZoomLevel: 'fit',
          secondaryZoomLevel: 1,
          maxZoomLevel: 1,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, data, index) => {
          const mediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
          if (mediaItems[index]) {
            const el = mediaItems[index].querySelector('img');
            if (el) return el;
          }
          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
          const index = slide.data.id;
          const mediaItems = this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)');
          if (mediaItems[index]) {
            const el = mediaItems[index].querySelector('img');
            if (el) return el.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          if (typeof window.pauseAllMedia === 'function') {
            window.pauseAllMedia(this);
          }
          if (this.sliderInstance && this.sliderInstance.slider) {
            const currIndex = this.lightbox.pswp.currIndex;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType !== 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          // Close button - top right
          this.lightbox.pswp.ui.registerElement({
            name: 'custom-close',
            ariaLabel: 'Close',
            order: 20,
            isButton: true,
            html: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: () => this.lightbox.pswp.close(),
          });

          // Navigation arrows
          if (dataSource.length > 1) {
            // Prev button - chevron left
            this.lightbox.pswp.ui.registerElement({
              name: 'custom-prev',
              ariaLabel: 'Previous',
              order: 10,
              isButton: true,
              html: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>',
              onClick: () => this.lightbox.pswp.prev(),
            });
            
            // Next button - chevron right
            this.lightbox.pswp.ui.registerElement({
              name: 'custom-next',
              ariaLabel: 'Next',
              order: 11,
              isButton: true,
              html: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>',
              onClick: () => this.lightbox.pswp.next(),
            });
          }
        });

        this.lightbox.init();

        // CLICK HANDLERS - Always attach, check mobile INSIDE each handler
        allMedia.forEach((mediaItem, index) => {
          const mediaType = mediaItem.dataset.mediaType || 'image';
          
          if (mediaType === 'image') {
            const zoomButton = mediaItem.querySelector('.js-photoswipe--zoom');
            if (zoomButton) {
              zoomButton.addEventListener('click', function(e) {
                if (window.innerWidth <= 767) return; // No popup on mobile
                e.preventDefault();
                e.stopPropagation();
                self.openLightbox(index);
              });
            }

            const zoomIcon = mediaItem.querySelector('.product__media-icon');
            if (zoomIcon) {
              zoomIcon.addEventListener('click', function(e) {
                if (window.innerWidth <= 767) return; // No popup on mobile
                e.preventDefault();
                e.stopPropagation();
                self.openLightbox(index);
              });
            }

            const container = mediaItem.querySelector('.product__media-container');
            if (container) {
              container.addEventListener('click', function(e) {
                if (window.innerWidth <= 767) return; // No popup on mobile
                if (e.target.closest('button')) return;
                e.preventDefault();
                e.stopPropagation();
                self.openLightbox(index);
              });
            }

            const img = mediaItem.querySelector('img');
            if (img) {
              img.style.cursor = 'pointer';
              img.addEventListener('click', function(e) {
                if (window.innerWidth <= 767) return; // No popup on mobile
                e.preventDefault();
                e.stopPropagation();
                self.openLightbox(index);
              });
            }
          }
        });
      }

      openLightbox(index) {
        if (this.lightbox) {
          this.lightbox.loadAndOpen(index);
        }
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          const adjustedIndex = slideIndex > 0 ? slideIndex - 1 : 0;
          this.sliderInstance.slider.slideToLoop(adjustedIndex);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
